/** @jsx jsx */
import { jsx } from '@balance-web/core';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { UseComboboxProps, UseComboboxStateChange } from 'downshift';
import { useCombobox } from 'downshift';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import type { Range } from '@tanstack/react-virtual';
import { useFieldContext } from '@balance-web/field';
import { PopoverDialog } from '@balance-web/popover';
import { DefaultTextPropsProvider, Text } from '@balance-web/text';
import { useForkedRef, wrapHandlers } from '@balance-web/utils';
import { Divider } from '@balance-web/divider';
import { theme } from '@balance-web/theme';

import { ComboboxContext } from './context';
import {
  defaultGetMessageText,
  defaultItemToDisabled,
  defaultItemToLabel,
  defaultItemToValue,
  makeDefaultItemRenderer,
  makeDefaultStateReducer,
} from './defaults';
import {
  Input,
  InputClearButton,
  InputContainer,
  InputDivider,
  InputLoadingIndicator,
  InputToggleButton,
  Menu,
  MenuItem,
  MenuItemSlot,
} from './styled-components';
import type { ComboboxProps, FocusOnMountBehaviour } from './types';
import { getItemsStatus, useComboboxPopper, useMaxMenuHeight } from './utils';
import { useComboboxState } from './useComboboxState';
import { useItemMap } from './useItemMap';

const noop = () => {};

/**
 * @param props.environment This prop is only useful if you're rendering the combobox within a different window context from where your JavaScript is running; for example, an iframe or a shadow-root.
 */
export function Combobox<Item>(props: ComboboxProps<Item>) {
  if (props.readOnly) {
    return <Text size={props.size}>{props.inputValue}</Text>;
  }

  return <DownshiftCombobox {...props} />;
}

function DownshiftCombobox<Item>(props: Omit<ComboboxProps<Item>, 'readOnly'>) {
  const {
    mode = 'field',
    canLoadMore,
    disabled = false,
    getMessageText = defaultGetMessageText,
    inputValue,
    itemRenderer: consumerItemRenderer,
    itemHeight: consumerItemHeight,
    footerItemHeight,
    items,
    footerItem,
    itemToDisabled = defaultItemToDisabled,
    itemToLabel = defaultItemToLabel,
    itemToValue = defaultItemToValue,
    loadingState = 'idle',
    onChange,
    onClear: consumerClearHandler,
    onInputChange,
    onLoadMore,
    placeholder,
    size = 'medium',
    menuWidth,
    stateReducer,
    value: selectedItem,
    environment,
    focusOnMount,
    inputRef: consumerInputRef,
    onUserInteractionButNoDataChange,
    stickyIndexes,
    itemToSelectable,
  } = props;

  const itemsWithOptionalFooter = footerItem
    ? [...items, footerItem]
    : [...items];

  // INTERNAL VALUES
  // ------------------------------

  const fieldContext = useFieldContext();
  const [isFocused, setFocused] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState(menuWidth ?? 0);
  const inputRef = useRef<HTMLInputElement>(null);
  const forkedInputRef = useForkedRef<HTMLInputElement>(
    inputRef,
    consumerInputRef || null
  );
  const menuRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  const focusMountMountRef = useRef<{
    behaviour?: FocusOnMountBehaviour;
    consumed: boolean;
  }>({
    behaviour: focusOnMount,
    consumed: false,
  });

  const activeStickyIndexRef = React.useRef(0);

  const isSticky = stickyIndexes
    ? (index: number) => {
        return stickyIndexes.includes(index);
      }
    : undefined;

  const isActiveSticky = stickyIndexes
    ? (index: number) => {
        return activeStickyIndexRef.current === index;
      }
    : undefined;

  const consumeFocusOnMount = () => {
    focusMountMountRef.current.consumed = true;
  };

  useEffect(function focusInputFieldOnMount() {
    if (
      focusMountMountRef.current.behaviour === 'focus' ||
      focusMountMountRef.current.behaviour === 'focus-and-open'
    ) {
      inputRef.current?.focus();
    }
  }, []);

  // INITIALISE CONTEXT
  // ------------------------------

  const context = useComboboxState({
    isItemDisabled: disabled,
    isFocused,
    isOpen,
    itemHeight: consumerItemHeight,
    size,
  });

  // PREPARE TRANSFORMERS
  // ------------------------------

  // handle downshift nullable arg type, simplifies consumer API
  const itemToString = useCallback(
    (item: Item | null) => {
      return item ? itemToLabel(item) : '';
    },
    [itemToLabel]
  );

  // the default state reducer adjusts downshift behaviours. not possible in
  // destructuring because we need the `itemToLabel` supporting prop
  const defaultStateReducer = useMemo(() => {
    return makeDefaultStateReducer(
      itemToLabel,
      itemToValue,
      items,
      focusMountMountRef.current,
      consumeFocusOnMount,
      itemToSelectable
    );
  }, [itemToLabel, itemToValue, items]);

  // create the default component. not possible in destructuring because we need
  // the `itemTo*` supporting props
  const defaultItemRenderer = useMemo(() => {
    return makeDefaultItemRenderer(itemToLabel, itemToDisabled);
  }, [itemToDisabled, itemToLabel]);

  const itemRenderer =
    mode === 'cell'
      ? (item: Item) => {
          return (
            <>
              {/*
                  NOTE: This is a sad hack.

                  This fake button is used as a tab target when combobox is rendered in an AG Grid cell,
                    otherwise sometimes it can't find a tab target and tabs out of the window entirely.

                  A better fix might be based around capturing and containing the event in specific
                    scenarios but nothing has worked so far.
              */}
              <button></button>
              {consumerItemRenderer
                ? consumerItemRenderer(item)
                : defaultItemRenderer(item)}
            </>
          );
        }
      : consumerItemRenderer || defaultItemRenderer;

  const rowVirtualizer = useVirtualizer({
    estimateSize: useCallback(
      (index: number) => {
        return typeof context.itemHeight === 'function'
          ? context.itemHeight(index)
          : context.itemHeight;
      },
      [context]
    ),
    count: canLoadMore ? items.length + 1 : items.length,
    getScrollElement: () => {
      return menuRef.current;
    },
    overscan: 4,
    /**
     * This logic is lifted directly from https://tanstack.com/virtual/latest/docs/framework/react/examples/sticky?panel=code
     */
    rangeExtractor: useCallback(
      (range: Range) => {
        if (!stickyIndexes) {
          return [...defaultRangeExtractor(range)];
        }

        activeStickyIndexRef.current =
          [...stickyIndexes].reverse().find((index) => {
            return range.startIndex >= index;
          }) ?? 0;

        const next = new Set([
          activeStickyIndexRef.current,
          ...defaultRangeExtractor(range),
        ]);

        return [...next].sort((a, b) => {
          return a - b;
        });
      },
      [stickyIndexes]
    ),
  });

  const loadMoreIfPossible = () => {
    const virtualItems = rowVirtualizer.getVirtualItems();

    const lastItem = virtualItems[virtualItems.length - 1];

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= items.length - 1 &&
      canLoadMore &&
      onLoadMore &&
      loadingState !== 'loadingMore'
    ) {
      onLoadMore();
    }
  };

  useEffect(() => {
    loadMoreIfPossible();
  }, []);

  // PREPARE DOWNSHIFT
  // ------------------------------

  // defer "changes" to the second argument: consumers may need access to it,
  // but only for advanced cases
  const handleInputValueChange = useCallback(
    (changes: UseComboboxStateChange<Item>) => {
      // coercion removes `undefined` — deviate from Downshift `inputValue` to
      // simplify the Balance API
      onInputChange(changes.inputValue || '', changes);
    },
    [onInputChange]
  );

  // augment the consumer's clear handler to focus the input. we use the
  // nullable type of `onClear` for conditional rendering
  const onClear = ((e) => {
    if (!selectedItem || !consumerClearHandler) {
      return null;
    }

    return () => {
      const inputElement = inputRef.current;
      if (inputElement) {
        inputElement.focus();
      }
      consumerClearHandler();
    };
  })();

  // defer "changes" to the second argument: consumers may need access to it,
  // but only for advanced cases
  const handleSelectedItemChange = useCallback(
    (changes: UseComboboxStateChange<Item>) => {
      const selectedItem = changes.selectedItem;
      /**
       * Intercept the _internalClear value and send null to ocnsumer onChange
       *  if applicable.
       */
      if (
        selectedItem !== null &&
        selectedItem !== undefined &&
        typeof selectedItem === 'object' &&
        'id' in selectedItem &&
        selectedItem.id === '_internalClear'
      ) {
        if (onClear) {
          onClear();
        }
      } else {
        /**
         * Normal flow.
         */
        onChange(changes.selectedItem || null, changes);
      }
    },
    [onChange, onClear]
  );
  // replace default handler with virtualizer
  const scrollToIndex = useCallback(
    (index: number) => {
      // Ignore scroll request when hover over footerItem since its position is fixed
      if (index === -1 || (footerItem && index === items.length)) {
        return;
      }
      rowVirtualizer.scrollToIndex(index, {
        align: 'end',
      });
    },
    [footerItem, items.length, rowVirtualizer]
  );
  const handleHighlightedIndexChange = useCallback(
    ({ highlightedIndex, type }: UseComboboxStateChange<Item>) => {
      if (
        /**
         * We only want to scroll into view on keyboard interactions
         *  because on mouse move there is an incorrect change in
         *  highlightedIndex when the mouse moves to the bottom of
         *  the last visible item in the list which causes a scroll.
         *  This is likely some edge case because of virtualisation.
         */
        type !== useCombobox.stateChangeTypes.ItemMouseMove &&
        highlightedIndex !== undefined &&
        highlightedIndex > -1 &&
        highlightedIndex < itemsWithOptionalFooter.length
      ) {
        scrollToIndex(highlightedIndex);
      }
    },
    [itemsWithOptionalFooter.length, scrollToIndex]
  );
  // replace default handler with virtualizer
  const handleIsOpenChange = useCallback(
    (changes: UseComboboxStateChange<Item>) => {
      if (typeof changes.isOpen === 'boolean') {
        setOpen(changes.isOpen);
      }
    },
    []
  );
  // selection/navigation handling
  const downshiftConfig: UseComboboxProps<Item> = {
    inputId: fieldContext.id,
    inputValue,
    isOpen,
    items: itemsWithOptionalFooter,
    itemToString,
    selectedItem: selectedItem || null,
    stateReducer: stateReducer || defaultStateReducer,
    defaultIsOpen: true,
    scrollIntoView: noop, // noop prevents conflicts between downshift and the virtualizer
    onInputValueChange: handleInputValueChange,
    onIsOpenChange: handleIsOpenChange,
    onSelectedItemChange: handleSelectedItemChange,
    onHighlightedIndexChange: handleHighlightedIndexChange,
    isItemDisabled: itemToDisabled,
    onStateChange: (changes) => {
      const didUserInteract =
        changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
        changes.type === useCombobox.stateChangeTypes.ItemClick ||
        changes.type === useCombobox.stateChangeTypes.InputBlur;

      const dataDidNotChange =
        changes.highlightedIndex === -1 && !changes.isOpen;

      if (
        onUserInteractionButNoDataChange &&
        didUserInteract &&
        dataDidNotChange
      ) {
        onUserInteractionButNoDataChange();
      }
    },
  };
  // when working with a different different window object such as from inside an iframe https://github.com/downshift-js/downshift#environment
  if (environment) {
    downshiftConfig.environment = environment;
  }

  const {
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    openMenu,
    highlightedIndex,
  } = useCombobox(downshiftConfig);

  useEffect(() => {
    if (isOpen && highlightedIndex !== undefined && highlightedIndex > 1) {
      rowVirtualizer.scrollToIndex(highlightedIndex, { align: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // INTERNAL HANDLERS
  // ------------------------------

  // track the container width, which is passed onto the dialog
  useEffect(() => {
    if (menuWidth) {
      return;
    }

    const element = inputContainerRef.current;
    if (element) {
      setContainerWidth((value) => {
        // set initial value + get latest value when the menu opens
        if (!value || isOpen) {
          return element.offsetWidth;
        }
        // react won't rerender unless the value changes
        return value;
      });
    }
  }, [isOpen, menuWidth]);

  const onFocus = wrapHandlers(props.onFocus, () => {
    setFocused(true);
    // open the menu on focus
    if (!isOpen) {
      openMenu();
    }
  });
  const onBlur = wrapHandlers(props.onBlur, () => {
    setFocused(false);
  });

  // setup popper (position handling)
  const {
    popperAnchorRef,
    popperPopoverRef,
    styles,
    attributes,
  } = useComboboxPopper(isOpen);

  // RESOLVE INTERFACE
  // ------------------------------

  // prepare common map for default items and regular items
  const itemMap = useItemMap({
    getItemProps,
    itemRenderer,
    itemToValue,
    itemToSelectable,
    items,
    selectedItem,
    isSticky,
    isActiveSticky,
  });

  // prepare menu content for each state
  const menuContent = (() => {
    // we need a reference to the menu element to prepare accessibility
    // handlers, but we don't want the menu content to be focusable until the
    // menu is open
    if (!isOpen) {
      return null;
    }

    // items aren't available for some reason
    const status = getItemsStatus({
      inputValue,
      loadingState,
      itemCount: items.length,
    });

    if (status) {
      return (
        <MenuItemSlot style={{ height: 40 }}>
          <Text color="dim">{getMessageText(status, inputValue)}</Text>
        </MenuItemSlot>
      );
    }

    // render items when available
    if (items.length || footerItem) {
      return (
        <Fragment>
          {rowVirtualizer.getVirtualItems().map(itemMap)}
          <div
            key="total-size"
            style={{
              height: rowVirtualizer.getTotalSize(),
            }}
          />
        </Fragment>
      );
    }

    // if we reach this point something's gone wrong...
    return null;
  })();

  // the element that sits beside the disclosure arrow
  const inputAccessoryElement = (() => {
    if (loadingState === 'loading') {
      return <InputLoadingIndicator />;
    }
    if (onClear) {
      return (
        <InputClearButton
          data-combobox-clear-button
          aria-label="clear selection"
          onClick={onClear}
        />
      );
    }
    return null;
  })();

  // GETTER CONFIGS
  // ------------------------------

  const inputGetterConfig = {
    'aria-describedby': fieldContext['aria-describedby'],
    'aria-invalid': fieldContext.invalid,
    'aria-labelledby': fieldContext.id,
    disabled,
    onBlur,
    onFocus,
    placeholder,
    ref: forkedInputRef,
  };

  const containerRef = useForkedRef(inputContainerRef, popperAnchorRef);
  const comboboxGetterConfig = {
    'data-invalid': fieldContext.invalid,
    ref: containerRef,
    focusRing: mode === 'cell' ? 'bold' : 'normal',
  } as const;

  const maxMenuHeight = useMaxMenuHeight(context.itemHeight);

  const menuGetterConfig = {
    ref: menuRef,
    style: { maxHeight: maxMenuHeight },
  };

  const PopOver = useCallback(
    ({ children }) => {
      if (mode === 'field') {
        return (
          <PopoverDialog
            ref={popperPopoverRef}
            isVisible={isOpen}
            size={size}
            style={{ ...styles.popper, width: containerWidth }}
            {...attributes.popper}
          >
            {children}
          </PopoverDialog>
        );
      }

      return (
        <div
          style={{
            width: containerWidth,
            background: theme.palette.background.dialog,
            boxShadow: theme.shadow.medium,
            borderRadius: theme.radii.small,
          }}
        >
          {children}
        </div>
      );
    },
    [
      containerWidth,
      isOpen,
      mode,
      // Don't mess with this dep or it will break popover position inside modals
      styles?.popper?.transform,
    ]
  );

  const resolvedFooterItemHeight =
    footerItemHeight ||
    (typeof context.itemHeight === 'number' ? context.itemHeight : 40);

  // COMPOSITION
  // ------------------------------

  return (
    <ComboboxContext.Provider value={context}>
      <Fragment>
        <InputContainer {...comboboxGetterConfig}>
          <Input {...getInputProps(inputGetterConfig)} />
          {inputAccessoryElement}
          <InputDivider />
          <InputToggleButton
            aria-label="toggle menu"
            {...getToggleButtonProps()}
          />
        </InputContainer>
        <PopOver>
          <DefaultTextPropsProvider size="small" weight="medium">
            <Menu
              {...getMenuProps({
                ...menuGetterConfig,
                onScroll: () => {
                  if (!menuRef.current) {
                    return;
                  }

                  const isAtBottom =
                    menuRef.current.scrollTop + menuRef.current.clientHeight >=
                    menuRef.current.scrollHeight -
                      // Loading element height
                      30;

                  if (isAtBottom) {
                    loadMoreIfPossible();
                  }
                },
              })}
            >
              {menuContent}
            </Menu>
            {footerItem && isOpen ? (
              <div css={{ height: resolvedFooterItemHeight }}>
                <Divider />
                <MenuItem
                  key={itemToValue(footerItem)}
                  isItemSelectable={true}
                  style={{
                    height: resolvedFooterItemHeight,
                  }}
                  {...getItemProps({
                    isItemDisabled: false,
                    isSelected: false,
                    index: items.length, // Last item points to the footer
                    item: footerItem,
                    // Since the 'footerItem' sits outside of 'Downshift Menu', usual 'onSelectedItemChange'
                    // doesn't work and hence we need to rely on 'onMouseDown' event to update the selected item.
                    // https://github.com/downshift-js/downshift/issues/287#issuecomment-512855962
                    onMouseDown: () => {
                      onChange(footerItem || null, {
                        // @ts-ignore
                        type: '__item_click__', // TODO: Use UseComboboxStateChangeTypes.itemClick when the export is fixed in downshift
                        highlightedIndex: items.length, // Last item points to the footer
                        inputValue: '',
                        selectedItem: footerItem,
                      });
                    },

                    // Do not set aria-selected manually here, it's controlled by downshift
                  })}
                  isMemoized={false} // Footer MenuItem should rerender when children change
                >
                  {itemRenderer(footerItem)}
                </MenuItem>
              </div>
            ) : null}
          </DefaultTextPropsProvider>
        </PopOver>
      </Fragment>
    </ComboboxContext.Provider>
  );
}
